import { deleteCookie, getCookie, setCookie } from 'cookies-next';

/**
 * Unified cookie service for authentication
 * Uses buyer_ prefix for all cookies to maintain compatibility
 */

const COOKIE_KEYS = {
  JWT_TOKEN: 'buyer_jwt_token',
  REFRESH_TOKEN: 'buyer_refresh_token',
  ACTIVE_ROLE: 'buyer_active_role',
  SELECTED_SHIPTO_ID: 'buyer_selected_shipto_id',
  SELECTED_SHIPTO_DC: 'buyer_selected_shipto_dc',
} as const;

/**
 * Unified cookie service for authentication
 */
export class CookieService {
  /**
   * Set authentication tokens in cookies
   */
  static setAuthTokens(token: string, refreshToken: string): void {
    setCookie(COOKIE_KEYS.JWT_TOKEN, token);
    setCookie(COOKIE_KEYS.REFRESH_TOKEN, refreshToken);
  }

  /**
   * Set user session data in cookies
   */
  static setSessionData(role: string, shipToId = '', shipToDc = ''): void {
    setCookie(COOKIE_KEYS.ACTIVE_ROLE, role);

    if (shipToId) {
      setCookie(COOKIE_KEYS.SELECTED_SHIPTO_ID, shipToId);
    }
    if (shipToDc) {
      setCookie(COOKIE_KEYS.SELECTED_SHIPTO_DC, shipToDc);
    }
  }

  /**
   * Set complete authentication data (tokens + session)
   */
  static setAuthData(
    token: string,
    refreshToken: string,
    role: string,
    shipToId = '',
    shipToDc = ''
  ): void {
    this.setAuthTokens(token, refreshToken);
    this.setSessionData(role, shipToId, shipToDc);
  }

  /**
   * Get JWT token from cookies
   */
  static getJwtToken(): string | undefined {
    const token = getCookie(COOKIE_KEYS.JWT_TOKEN);
    return typeof token === 'string' ? token : undefined;
  }

  /**
   * Get refresh token from cookies
   */
  static getRefreshToken(): string | undefined {
    const token = getCookie(COOKIE_KEYS.REFRESH_TOKEN);
    return typeof token === 'string' ? token : undefined;
  }

  /**
   * Get active role from cookies
   */
  static getActiveRole(): string | undefined {
    const role = getCookie(COOKIE_KEYS.ACTIVE_ROLE);
    return typeof role === 'string' ? role : undefined;
  }

  /**
   * Get selected ship-to ID from cookies (buyer only)
   */
  static getSelectedShipToId(): string | undefined {
    const shipToId = getCookie(COOKIE_KEYS.SELECTED_SHIPTO_ID);
    return typeof shipToId === 'string' ? shipToId : undefined;
  }

  /**
   * Get selected ship-to DC from cookies (buyer only)
   */
  static getSelectedShipToDc(): string | undefined {
    const shipToDc = getCookie(COOKIE_KEYS.SELECTED_SHIPTO_DC);
    return typeof shipToDc === 'string' ? shipToDc : undefined;
  }

  /**
   * Clear all authentication-related cookies
   */
  static clearAllAuthCookies(): void {
    deleteCookie(COOKIE_KEYS.JWT_TOKEN);
    deleteCookie(COOKIE_KEYS.REFRESH_TOKEN);
    deleteCookie(COOKIE_KEYS.ACTIVE_ROLE);
    deleteCookie(COOKIE_KEYS.SELECTED_SHIPTO_ID);
    deleteCookie(COOKIE_KEYS.SELECTED_SHIPTO_DC);
  }

  /**
   * Clear only authentication tokens (keep session data)
   */
  static clearAuthTokens(): void {
    deleteCookie(COOKIE_KEYS.JWT_TOKEN);
    deleteCookie(COOKIE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  static isAuthenticated(): boolean {
    return !!(this.getJwtToken() && this.getRefreshToken());
  }

  /**
   * Get all authentication data from cookies
   */
  static getAllAuthData() {
    return {
      jwtToken: this.getJwtToken(),
      refreshToken: this.getRefreshToken(),
      activeRole: this.getActiveRole(),
      selectedShipToId: this.getSelectedShipToId(),
      selectedShipToDc: this.getSelectedShipToDc(),
    };
  }
}

export const BuyerCookieService = CookieService;
export const AdminCookieService = CookieService;
